import React, { FormEvent } from "react";
import { Grid, MenuItem } from "@mui/material";
import { get } from "lodash";
import { connect } from "react-redux";
import { Field, FieldProps } from "formik";
import { ConfigPropExtenderHoc } from "../../../../../common/StaticConfigProvider";
import OptionalToolTip from "../../../../../common/toolTip/OptionalToolTip";
import TextInput from "../../../../../common/formElements/TextInput";
import SelectDropDown from "../../../../../common/formElements/SelectDropDown";

interface MenuItem {
  value: string;
  display: string;
}

interface ConfigOption {
  label: string;
  field: string;
  labelTooltip: string;
  onInput?: (e: FormEvent<HTMLInputElement>) => void;
  tooltip?: string;
  selects?: MenuItem[];
}

interface ConfigOptionHoc {
  label: string;
  field: string;
  selects?: MenuItem[];
  onInput?: (e: FormEvent<HTMLInputElement>) => void;
  labelTooltip: string;
}

interface ConfigurationProps {
  options: ConfigOption[];
  isUpdateAllowed?: boolean;
  shouldHideField?: (fieldName: string) => boolean;
}

interface ConfigurationHocProps {
  options: ConfigOptionHoc[];
  toolTips?: { terminals?: { options?: Record<string, string> } };
  shouldHideField?: (fieldName: string) => boolean;
}

const ConfigurationTimeouts = ({
  options,
  isUpdateAllowed,
  shouldHideField,
}: ConfigurationProps) => {
  // Filter out options that should be hidden
  const visibleOptions = shouldHideField 
    ? options.filter(option => !shouldHideField(option.field))
    : options;

  return (
    <Grid container spacing={2}>
      {visibleOptions.map((option, idx) => {
        if (option.selects) {
          return (
            <Grid key={idx} item xs={12} md={4}>
              <SelectDropDown
                fieldname={option.field}
                label={option.labelTooltip}
                options={option.selects.map((opt) => opt.value)}
                disabled={!isUpdateAllowed}
              />
            </Grid>
          );
        } else {
          return (
            <Grid key={idx} item xs={12} md={4}>
              <Field name={option.field}>
                {({ field }: FieldProps) => (
                  <OptionalToolTip tooltip={option.tooltip}>
                    <TextInput
                      fieldname={field.name}
                      label={option.label}
                      onInput={
                        option.onInput ? (e) => option.onInput?.(e) : undefined
                      }
                      disabled={!isUpdateAllowed}
                    />
                  </OptionalToolTip>
                )}
              </Field>
            </Grid>
          );
        }
      })}
    </Grid>
  );
};

const ConnectedConfigurationTimeouts = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: ConfigurationHocProps) => {
    return {
      ...ownProps,
      shouldHideField: ownProps.shouldHideField,
      options: (ownProps?.options || []).map(
        ({ label, field, labelTooltip, selects, onInput }) => ({
          label,
          field,
          labelTooltip,
          selects,
          onInput,
          tooltip: get(
            ownProps,
            `toolTips.terminals.options.configuration.["${labelTooltip}"]`
          ),
        })
      ),
    };
  }
)(ConfigurationTimeouts);

const ConnectedConfigurationTimeoutsHoc = (props: ConfigurationProps) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedConfigurationTimeouts {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedConfigurationTimeoutsHoc;
